import { type CustomValidateFunction } from '@/components/forms/types';
import type { ErrorHandlerFunction, HandledResponse } from '@/api/generator';
import type { AggregatedAjvError } from '@/validation/createSchemaValidator';
import { I18nKey } from '@/utils/typings';
import { truthy } from '@/utils/helper';
import { isForbiddenError, isSemanticError, isSyntacticError } from '@/errorHandling';
import { t } from 'i18next';

export function mapBackendErrorToAjvErrors(error: Error) {
  // Backend accesses members with capital letters in beginning, so we have to lowercase them to ensure correct association to form fields in DTO
  function camelCasePath(path: string) {
    return path
      .split('.')
      .map((s) => s[0].toLowerCase() + s.slice(1))
      .join('.');
  }

  const validationErrors =
    isSemanticError(error) || isSyntacticError(error) || isForbiddenError(error)
      ? error.errorResponse?.validationErrors
      : undefined;

  const errors = validationErrors
    ?.map((validationError) =>
      validationError.memberNames?.map<AggregatedAjvError>((path) => ({
        path: camelCasePath(path),
        errors: [t(validationError.errorMessage as I18nKey)],
      })),
    )
    .flat()
    .filter(truthy);

  return errors;
}

export function backendValidator<TValues>(
  apiCall: (
    requestParameters: TValues,
    errorHandlers: ErrorHandlerFunction[],
  ) => Promise<HandledResponse<unknown>>,
): CustomValidateFunction<TValues> {
  return async function validate(requestParameters: TValues) {
    let errors: AggregatedAjvError[] | undefined;

    await apiCall(requestParameters, [
      (error) => {
        errors = mapBackendErrorToAjvErrors(error);
      },
    ]);

    return errors;
  };
}
